import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { HiringStep } from 'lib/api'
import {
  DEFAULT_STEP_COLOR,
  DEFAULT_STEP_TYPE,
  DEFAULT_STEP_ESTIMATED_DURATION,
  getCategoryDefaultName,
  resolveNewStepFormData,
} from 'constants/hiringProcess'

// Steps that cannot be modified (locked steps)
const IMMUTABLE_STEP_TYPES = ['initial', 'scanning']

const isStepImmutable = (stepType: string): boolean => {
  return IMMUTABLE_STEP_TYPES.includes(stepType)
}

interface HiringStepModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (step: HiringStep, positionOrder?: number) => void | Promise<void>
  step: HiringStep | null
  stepTypes: Array<{
    value: string
    label: string
    icon: string
    description: string
  }>
  stepColors: string[]
  /** When adding a step for an offer: options for where to insert (e.g. "Before Rejected"). Default = before Rejected. */
  positionOptions?: Array<{ value: number; label: string }>
  defaultPosition?: number
}


export function HiringStepModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  step,
  stepTypes,
  stepColors,
  positionOptions,
  defaultPosition
}: HiringStepModalProps) {
  const { t } = useTranslation()
  const showPosition = !step && positionOptions && positionOptions.length > 0
  const [selectedPosition, setSelectedPosition] = useState<number>(defaultPosition ?? (positionOptions?.[0]?.value ?? 0))

  // Only set initial position when modal opens — don't reset when parent re-renders (positionOptions is recreated each time) or we overwrite the user's dropdown choice
  useEffect(() => {
    if (isOpen && showPosition) {
      setSelectedPosition(defaultPosition ?? positionOptions?.[0]?.value ?? 0)
    }
  }, [isOpen]) // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when open/close, not on positionOptions change

  // Step form state
  const [stepForm, setStepForm] = useState({
    name: '',
    description: '',
    step_type: DEFAULT_STEP_TYPE,
    color: DEFAULT_STEP_COLOR,
    is_required: true,
    estimated_duration: DEFAULT_STEP_ESTIMATED_DURATION,
    is_active: true
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (step) {
      setStepForm({
        name: step.name || '',
        description: step.description || '',
        step_type: step.step_type || DEFAULT_STEP_TYPE,
        color: step.color || DEFAULT_STEP_COLOR,
        is_required: step.is_required !== undefined ? step.is_required : true,
        estimated_duration: step.estimated_duration || DEFAULT_STEP_ESTIMATED_DURATION,
        is_active: step.is_active !== undefined ? step.is_active : true
      })
    } else {
      // Reset form for new step
      setStepForm({
        name: '',
        description: '',
        step_type: DEFAULT_STEP_TYPE,
        color: DEFAULT_STEP_COLOR,
        is_required: true,
        estimated_duration: DEFAULT_STEP_ESTIMATED_DURATION,
        is_active: true
      })
    }
  }, [step, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    const { isCustom, ...resolvedFields } = resolveNewStepFormData(stepForm, t)

    const stepData: HiringStep = {
      ...step,
      ...resolvedFields,
      actions: step?.actions || [],
      order: step?.order || 0,
      template: step?.template || 0,
      id: step?.id || Date.now(),
      action_count: Array.isArray(step?.actions) ? step.actions.length : 0,
      created_at: step?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: step?.created_by
    }

    setIsSubmitting(true)
    try {
      await onSuccess(stepData, showPosition ? selectedPosition : undefined)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isImmutable = step ? isStepImmutable(step.step_type) : false
  const isAdding = !step

  const displayStepTypes = useMemo(() => {
    if (!step || stepTypes.some((type) => type.value === step.step_type)) {
      return stepTypes
    }
    return [
      ...stepTypes,
      {
        value: step.step_type,
        label: t(`hiringProcess.stepTypes.${step.step_type}`, step.step_type),
        icon: '⚙️',
        description: t(`hiringProcess.stepDescriptions.${step.step_type}`, ''),
      },
    ]
  }, [step, stepTypes, t])

  const footerContent = isImmutable ? (
    <div className="flex justify-end">
      <Button type="button" onClick={onClose}>
        {t('common.close', 'Close')}
      </Button>
    </div>
  ) : (
    <div className="flex justify-end gap-3">
      <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" form="step-form" loading={isSubmitting} disabled={isSubmitting}>
        {step ? t('common.update') : t('common.create')}
      </Button>
    </div>
  )

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={step ? t('hiringProcess.editStep') : t('hiringProcess.createStep')}
        className="max-w-4xl"
        footer={footerContent}
      >
        {isImmutable ? (
          <div className="space-y-4">
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                <strong>{t('hiringProcess.immutableStepWarning', 'This step is locked and cannot be modified.')}</strong>
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">
                {t('hiringProcess.immutableStepExplanation', 'The initial, scanning, and screening steps are core system steps that must remain unchanged to ensure proper workflow functionality.')}
              </p>
            </div>
          </div>
        ) : (
        <form id="step-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Position (for offer steps only) */}
          {showPosition && (
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('hiringProcess.stepPosition', 'Step position')}
              </label>
              <select
                value={selectedPosition}
                onChange={(e) => setSelectedPosition(Number(e.target.value))}
                className="w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {positionOptions!.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
          {/* Category Selection */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
              {t('hiringProcess.stepCategory', 'Category')}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.isArray(displayStepTypes) && displayStepTypes.length > 0 ? displayStepTypes.map((stepType) => (
              <button
                key={stepType.value}
                type="button"
                onClick={() => {
                  const prevForm = stepForm
                  const nextForm = { ...stepForm, step_type: stepType.value }
                  const prevDefaultName = getCategoryDefaultName(prevForm.step_type, t)

                  if (isAdding && (!prevForm.name.trim() || prevForm.name === prevDefaultName)) {
                    nextForm.name = ''
                  }
                  if (isAdding && prevForm.name === prevDefaultName && stepType.value !== prevForm.step_type) {
                    nextForm.name = ''
                  }

                  setStepForm(nextForm)
                }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    stepForm.step_type === stepType.value
                      ? 'border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-900/40'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{stepType.icon}</span>
                    <span className="font-medium text-sm text-foreground">{stepType.label}</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{stepType.description}</p>
                </button>
              )) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">No categories available</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('hiringProcess.basicInformation')}
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hiringProcess.stepName')}
                  {isAdding && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({t('hiringProcess.optional', 'Optional')})
                    </span>
                  )}
                  {!isAdding && ' *'}
                </label>
                <Input
                  value={stepForm.name}
                  onChange={(e) => setStepForm({ ...stepForm, name: e.target.value })}
                  placeholder={
                    isAdding
                      ? getCategoryDefaultName(stepForm.step_type, t)
                      : t('hiringProcess.stepNamePlaceholder')
                  }
                  required={!isAdding}
                />
                {isAdding && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(
                      'hiringProcess.stepNameOptionalHint',
                      'Leave blank to use the category name. You can add duplicate steps of the same category.'
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hiringProcess.stepDescription')}
                  {isAdding && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({t('hiringProcess.optional', 'Optional')})
                    </span>
                  )}
                </label>
                <Textarea
                  value={stepForm.description}
                  onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
                  placeholder={t('hiringProcess.stepDescriptionPlaceholder')}
                  rows={3}
                />
                {isAdding && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(
                      'hiringProcess.stepCustomHint',
                      'A custom name or description makes this step deletable and unique in the pipeline.'
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hiringProcess.stepColor')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {Array.isArray(stepColors) && stepColors.length > 0 ? stepColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setStepForm({ ...stepForm, color })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        stepForm.color === color ? 'border-gray-900 dark:border-gray-100' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  )) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">No colors available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('hiringProcess.configuration')}
              </h3>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={stepForm.is_required}
                    onChange={(e) => setStepForm({ ...stepForm, is_required: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-blue-500 dark:bg-gray-800"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('hiringProcess.isRequired')}</span>
                </label>
                                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={stepForm.is_active}
                    onChange={(e) => setStepForm({ ...stepForm, is_active: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-blue-500 dark:bg-gray-800"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('hiringProcess.isActive')}</span>
                </label>
                {!stepForm.is_active && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    {t('hiringProcess.inactiveStepNote', 'Inactive steps will not appear in the hiring workflow (kanban), but remain visible and editable in the management page.')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
        )}
      </Modal>
    </>
  )
}
